import { afterNextRender, inject, Injectable } from '@angular/core';
import {
  Action,
  NgxsOnInit,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { AuthService } from './auth-service';
import { finalize, from, switchMap, tap } from 'rxjs';
import {
  AimiLocalStorageItem,
  AuthLogin,
  AuthLogOut,
  AuthPatchState,
} from './auth-action';
import { LoadingEnd, LoadingStart } from '../../core/loading.state';
import { User } from '@aimi/models';

export interface AuthStateModel {
  user: User | undefined;
  isLoggedIn: boolean | undefined;
}

@State<AuthStateModel>({
  name: 'AuthState',
  defaults: {
    user: undefined,
    isLoggedIn: undefined,
  },
})
@Injectable()
export class AuthState implements NgxsOnInit {
  #authService = inject(AuthService);
  #store = inject(Store);

  constructor() {
    afterNextRender(() => {
      const isLoggedIn = window?.localStorage.getItem(
        AimiLocalStorageItem.LOGIN_STATUS,
      );
      this.#store?.dispatch(
        new AuthPatchState({
          isLoggedIn: isLoggedIn === 'true',
        }),
      );
    });
  }

  @Selector() static user(state: AuthStateModel) {
    return state.user;
  }

  @Selector() static isLoggedIn(state: AuthStateModel) {
    return state.isLoggedIn;
  }

  ngxsOnInit(ctx: StateContext<any>): void {
    this.#authService.setupAuthStageChange();
  }

  @Action(AuthLogin)
  login(ctx: StateContext<AuthStateModel>) {
    ctx.dispatch(new LoadingStart(this.constructor.name));
    return from(this.#authService.googleSignInWithPopup()).pipe(
      switchMap(() => {
        return this.#authService.loginWithGoogle();
      }),
      tap((res) => {
        ctx.patchState({
          user: res.user,
        });
      }),
      finalize(() => ctx.dispatch(new LoadingEnd(this.constructor.name))),
    );
  }

  @Action(AuthLogOut)
  logout() {
    return this.#authService
      .logout()
      .pipe(tap(() => (window.location.href = '/')));
  }

  @Action(AuthPatchState)
  patchState(ctx: StateContext<AuthStateModel>, action: AuthPatchState) {
    ctx.patchState(action.payload);
  }
}
